import { useCallback, useEffect, useState } from 'react';

import { productionApi } from 'api/production';
import { getWeekNumber, getWeekYear } from 'utils/dateUtils/dateUtils';

/**
 * Hook to manage fetching and state for bulk apply configurations.
 * Handles loading configs by week, extracting pack groups, and building original slice identity map.
 *
 * @param {string|number} currentConfigId - ID of the current config (to exclude from results)
 * @param {Date|null} filterWeekDate - Currently selected week filter date
 * @param {boolean} isOpen - Whether the dialog is open
 * @returns {Object} Config loading state and data
 */
function useBulkApplyConfigs(isOpen, filterWeekDate, currentConfigId) {
    // All fetched configs (unfiltered)
    const [allConfigs, setAllConfigs] = useState([]);

    // Available pack groups for filter
    const [packGroups, setPackGroups] = useState([]);

    // Maps slice ID to its original (saved) name and displayType for matching across configs
    const [originalSliceIdentityById, setOriginalSliceIdentityById] = useState(new Map());

    // Set of copyable slice IDs (named+typed slices in the saved config, including deletions)
    const [copyableSliceIds, setCopyableSliceIds] = useState(new Set());

    // Loading state
    const [loading, setLoading] = useState(true);

    /**
     * Reset all state to initial values
     */
    const reset = useCallback(() => {
        setAllConfigs([]);
        setPackGroups([]);
        setOriginalSliceIdentityById(new Map());
        setCopyableSliceIds(new Set());
    }, []);

    // Fetch configs when dialog opens or week changes
    useEffect(() => {
        if (!isOpen || !filterWeekDate) return;

        const fetchConfigs = async () => {
            setLoading(true);

            try {
                // Fetch the saved state of current config to get original slice identities
                // This allows us to match slices even if names have been changed locally
                const savedCurrentConfig = await productionApi.getStorefrontConfigurationByID(currentConfigId);
                const savedSlices = savedCurrentConfig.data?.storefrontConfiguration?.slices || [];

                const identityById = new Map();
                const sliceIds = new Set();
                savedSlices.forEach((slice) => {
                    if (slice.id && slice.name && slice.displayType) {
                        identityById.set(slice.id, { name: slice.name, displayType: slice.displayType });
                        sliceIds.add(slice.id);
                    }
                });
                setOriginalSliceIdentityById(identityById);
                setCopyableSliceIds(sliceIds);

                // Build set of original names (non-blank) for matching
                const originalNames = new Set(savedSlices.map((s) => s.name).filter(Boolean));

                // Fetch all pack groups for the selected week
                const packGroupsResponse = await productionApi.getPackGroups({
                    week: getWeekNumber(filterWeekDate, 'YYYY-MM-DD'),
                    year: getWeekYear(filterWeekDate, 'YYYY-MM-DD'),
                });

                // Filter out non-standard pack groups (ex. "LS CHI1", "ST CHI1", "SM CHI1")
                const filteredPackGroups = packGroupsResponse.data.packGroups.filter(
                    (packGroup) => packGroup.name.split(' ').length === 1,
                );
                setPackGroups(filteredPackGroups);

                // Fetch configs for each pack group
                const configsByPackGroup = await Promise.all(
                    filteredPackGroups.map(async (packGroup) => {
                        try {
                            const response = await productionApi.getStorefrontConfigurationsByPackGroupID(packGroup.id);
                            return response.data?.configurations || [];
                        } catch (error) {
                            console.error(`Error fetching configs for pack group ${packGroup.id}:`, error);
                            return [];
                        }
                    }),
                );

                // Flatten all configs from all pack groups, exclude current config
                const allConfigMeta = configsByPackGroup.flat().filter((config) => config.id !== currentConfigId);

                // Fetch full config data for each config
                const fullConfigs = await Promise.all(
                    allConfigMeta.map(async (meta) => {
                        try {
                            const fullResponse = await productionApi.getStorefrontConfigurationByID(meta.id);
                            return fullResponse.data?.storefrontConfiguration || null;
                        } catch (error) {
                            console.error(`Error fetching config ${meta.id}:`, error);
                            return null;
                        }
                    }),
                );

                // Filter out any failed fetches
                const validConfigs = fullConfigs.filter(Boolean);
                setAllConfigs(validConfigs);

                // Return configs with matches for auto-selection
                return {
                    validConfigs,
                    originalNames,
                };
            } catch (error) {
                console.error('Error fetching configurations:', error);
                setAllConfigs([]);
                return { validConfigs: [], originalNames: new Set() };
            } finally {
                setLoading(false);
            }
        };

        fetchConfigs();
    }, [isOpen, filterWeekDate, currentConfigId]);

    return {
        allConfigs,
        packGroups,
        loading,
        originalSliceIdentityById,
        copyableSliceIds,
        reset,
    };
}

export default useBulkApplyConfigs;
